// frontend/src/pages/FinanceSupportTickets.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Visibility,
  AttachMoney,
  PendingActions,
  CheckCircle,
  Cancel,
  Refresh,
  Paid,
  AccountBalance
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const StatusChip = ({ status }) => {
  const colors = {
    'Open': { bg: '#ffebee', color: '#c62828' },
    'In Progress': { bg: '#fff3e0', color: '#ef6c00' },
    'Resolved': { bg: '#e8f5e9', color: '#2e7d32' },
    'Closed': { bg: '#e0e0e0', color: '#616161' }
  };
  return (
    <Chip 
      label={status} 
      size="small" 
      sx={{ 
        bgcolor: colors[status]?.bg, 
        color: colors[status]?.color, 
        fontWeight: 500,
        fontSize: '12px'
      }} 
    />
  );
};

const PriorityChip = ({ priority }) => {
  const colors = {
    'Low': { bg: '#e8f5e9', color: '#2e7d32' },
    'Medium': { bg: '#fff3e0', color: '#ef6c00' },
    'High': { bg: '#ffebee', color: '#c62828' },
    'Urgent': { bg: '#fce4ec', color: '#d81b60' }
  };
  return (
    <Chip 
      label={priority} 
      size="small" 
      sx={{ 
        bgcolor: colors[priority]?.bg, 
        color: colors[priority]?.color, 
        fontWeight: 500,
        fontSize: '12px'
      }} 
    />
  );
};

function FinanceSupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [resolution, setResolution] = useState('');
  const [updateStatus, setUpdateStatus] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [stats, setStats] = useState({ total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0 });
  const [filters, setFilters] = useState({ status: 'all', category: 'all', priority: 'all', search: '' });
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchTickets();
  }, [filters]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams(filters);
      const response = await axios.get(`${API_URL}/api/support/finance/tickets?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setTickets(response.data.data || []);
      setStats(response.data.stats || {});
    } catch (error) {
      showNotification('Failed to fetch tickets', 'error');
    } finally {
      setLoading(false);
    }
  };

  const viewTicketDetails = async (ticket) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/support/tickets/${ticket.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setSelectedTicket(response.data.data);
      setUpdateStatus(response.data.data.status);
      setResolution(response.data.data.resolution || '');
      setOpenDialog(true);
    } catch (error) {
      showNotification('Failed to load ticket details', 'error');
    }
  };

  const handleUpdateTicket = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/support/finance/tickets/${selectedTicket.id}`, 
        { status: updateStatus, resolution },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      showNotification('Ticket updated successfully', 'success');
      setOpenDialog(false);
      fetchTickets();
    } catch (error) {
      showNotification('Failed to update ticket', 'error');
    }
  };

  const showNotification = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Filter tickets by tab
  const filteredTickets = tickets.filter(ticket => {
    if (tabValue === 0) return true;
    if (tabValue === 1) return ticket.status === 'Open';
    if (tabValue === 2) return ticket.status === 'In Progress';
    if (tabValue === 3) return ticket.status === 'Resolved';
    return true;
  });

const StatCard = ({ title, value, icon, borderColor }) => (
  <Card
    sx={{
      bgcolor: "#fff",
      borderLeft: `6px solid ${borderColor}`,
      borderRadius: 2,
      boxShadow: 2,
      height: "100%",
    }}
  >
    <CardContent>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight="bold"
            color="text.primary"
          >
            {value}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            {title}
          </Typography>
        </Box>

        {icon}
      </Box>
    </CardContent>
  </Card>
);

  const categories = ['Profile', 'IT', 'Payroll', 'HR', 'Attendance', 'Leave', 'Other'];
  const priorities = ['Low', 'Medium', 'High', 'Urgent'];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight="600" sx={{ mb: 3 }}>
        Finance Support Tickets
      </Typography>
      
<Grid container spacing={3} sx={{ mb: 3 }}>
  <Grid item xs={12} sm={6} md={3}>
    <StatCard
      title="Total Tickets"
      value={stats.total}
      icon={<Paid sx={{ color: "#3b82f6" }} />}
      borderColor="#3b82f6"
    />
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <StatCard
      title="Open"
      value={stats.open}
      icon={<PendingActions sx={{ color: "#ef4444" }} />}
      borderColor="#ef4444"
    />
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <StatCard
      title="In Progress"
      value={stats.inProgress}
      icon={<PendingActions sx={{ color: "#f59e0b" }} />}
      borderColor="#f59e0b"
    />
  </Grid>

  <Grid item xs={12} sm={6} md={3}>
    <StatCard
      title="Resolved"
      value={stats.resolved}
      icon={<CheckCircle sx={{ color: "#10b981" }} />}
      borderColor="#10b981"
    />
  </Grid>
</Grid>

      {/* Tabs */}
          <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, newVal) => setTabValue(newVal)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="All Tickets" />
          <Tab label="Open" />
          <Tab label="In Progress" />
          <Tab label="Resolved" />
        </Tabs>
      </Paper>


      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            size="small"
            placeholder="Search by ticket # or subject..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            sx={{ width: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            >
              <MenuItem value="all">All</MenuItem>
              {categories.map(cat => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
            >
              <MenuItem value="all">All</MenuItem>
              {priorities.map(pri => (
                <MenuItem key={pri} value={pri}>{pri}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button onClick={fetchTickets} startIcon={<Refresh />} variant="outlined" size="small">
            Refresh
          </Button>
        </Box>
      </Paper>

      {/* Tickets Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#fafafa' }}>
            <TableRow>
              <TableCell>Ticket #</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography>Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : filteredTickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">No tickets found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTickets.map((ticket) => (
                <TableRow key={ticket.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="500">
                      {ticket.ticket_number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {ticket.employee?.firstName || 'N/A'} {ticket.employee?.lastName || ''}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {ticket.employee?.companyemail || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                      {ticket.subject}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={ticket.category} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <PriorityChip priority={ticket.priority} />
                  </TableCell>
                  <TableCell>
                    <StatusChip status={ticket.status} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(ticket.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => viewTicketDetails(ticket)}>
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Details Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        {selectedTicket && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography variant="h6">{selectedTicket.ticket_number}</Typography>
                <Box display="flex" gap={1}>
                  <StatusChip status={selectedTicket.status} />
                  <PriorityChip priority={selectedTicket.priority} />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Employee Details</Typography>
                  <Typography variant="body1" fontWeight="500">
                    {selectedTicket.employee?.firstName} {selectedTicket.employee?.lastName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {selectedTicket.employee?.companyemail}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Category & Assignment</Typography>
                  <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                    <Chip label={selectedTicket.category} size="small" />
                    <Chip 
                      label={`Assigned to: ${selectedTicket.assigned_to}`} 
                      size="small" 
                      variant="outlined" 
                      color={selectedTicket.assigned_to === 'Finance' ? 'primary' : 'default'}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Subject</Typography>
                  <Typography variant="body1" fontWeight="500">{selectedTicket.subject}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedTicket.description}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Resolution / Notes</Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Add resolution notes..."
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Update Status</InputLabel>
                    <Select value={updateStatus} onChange={(e) => setUpdateStatus(e.target.value)}>
                      <MenuItem value="Open">Open</MenuItem>
                      <MenuItem value="In Progress">In Progress</MenuItem>
                      <MenuItem value="Resolved">Resolved</MenuItem>
                      <MenuItem value="Closed">Closed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                  <Box display="flex" gap={2} mt={2}>
                    <Typography variant="caption" color="textSecondary">
                      Created: {new Date(selectedTicket.createdAt).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Last Updated: {new Date(selectedTicket.updatedAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
              <Button onClick={handleUpdateTicket} variant="contained" color="primary">
                Update Ticket
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default FinanceSupportTickets;